import { useState, useEffect, useMemo } from 'react'
import './index.scss'
import * as $ from 'jquery'
import { GrGroup } from 'react-icons/gr'
import { Loader } from 'components/loader'
import ButtonV3 from 'components/Buttonv3'
import { FeedbackError } from 'components/feedback/error'
import TacticalPanelService from 'services/v3/tacticalPanel.service'
import { StatusLabel } from './statusLabel'
import { LabelPrice } from './labelPrice'
import { statusColors } from 'helpers/status.colors'
import { Select } from 'components/select'
import { Legend } from './legend'

type QuotationModalProps = {
    isVisible: boolean
    onClose: () => void
    productId?: number
    user?: any
    hash: string
}

interface IQuotationResponse {
    product_code: string
    product_name: string
    product_brand: string
    client_id: string
    client_name: string
    seller_id: string
    seller_name: string
    id_dim_status: number
    status_name: string
    quotation_code: string
    date: string
    quotation_price: number
    winner_price: number
    color?: string
}

export const QuotationModal = ({
    isVisible,
    onClose,
    productId,
    user,
    hash,
}: QuotationModalProps) => {
    const [loadingTable, setLoadingTable] = useState(false)
    const [quotation, setQuotation] = useState<IQuotationResponse[] | null>(
        null
    )
    const [filters, setFilters] = useState<Set<string>>(
        new Set(['VENCEDOR', 'PERDEDOR'])
    )
    const [loadingFilters, setLoadingFilters] = useState(false)
    const [selectedSellerId, setSelectedSellerId] = useState<string>('')
    const [selectedClientId, setSelectedClientId] = useState<string>('')

    const modal = {
        icon: <GrGroup color="white" />,
        styles: { maxWidth: '80vw' },
        id: 'info-cotacoes',
        title: `Cotações`,
    }

    useEffect(() => {
        if (isVisible) {
            $('#' + modal.id).removeClass('fadeOutUp')
            $('#' + modal.id).addClass('fadeIn')
            $('#' + modal.id).modal('show')

            loadListProductQuotes()
        } else {
            $('#' + modal.id).removeClass('fadeIn')
            $('#' + modal.id).addClass('fadeOutUp')
            $('#' + modal.id).modal('hide')

            setSelectedSellerId('')
            setSelectedClientId('')
            setFilters(new Set(['VENCEDOR', 'PERDEDOR']))
        }
    }, [isVisible, user, productId])

    const loadListProductQuotes = () => {
        if (!user || !productId || !hash) return

        setLoadingTable(true)

        TacticalPanelService.ListProductQuotes(user.providerId, productId, hash)
            .then((result) => {
                setQuotation(result.data)
            })
            .catch((error) => {
                setQuotation(null)
                console.error(error)
            })
            .finally(() => {
                setLoadingTable(false)
            })
    }

    const convertStatus = (status: string): string => {
        const statusMap: Record<string, string> = {
            WINNER: 'VENCEDOR',
            LOSER: 'PERDEDOR',
            PENDING: 'PENDENTE',
            ORDERED: 'PEDIDO GERADO',
            NOT_OFFERED: 'NÃO OFERTADO',
            NOT_ORDERED: 'SEM PEDIDO',
            NOT_TYPED: 'NÃO DIGITADO',
            TYPED: 'DIGITADO',
        }
        return statusMap[status] || status
    }

    const filteredQuotations = useMemo(() => {
        if (!quotation) return []

        // return [...quotation]
        //     .sort(
        //         (a, b) =>
        //             new Date(b.date).getTime() - new Date(a.date).getTime()
        //     )
        //     .filter((q) => {
        //         const matchesStatus = filters.has(convertStatus(q.status_name))
        //         const matchesSeller = selectedSellerId
        //             ? q.seller_id === selectedSellerId
        //             : true
        //         const matchesClient = selectedClientId
        //             ? q.client_id === selectedClientId
        //             : true
        //         return matchesStatus && matchesSeller && matchesClient
        //     })
        return quotation.filter((q) => {
            const matchesStatus = filters.has(convertStatus(q.status_name))
            const matchesSeller = selectedSellerId
                ? q.seller_id === selectedSellerId
                : true
            const matchesClient = selectedClientId
                ? q.client_id === selectedClientId
                : true
            return matchesStatus && matchesSeller && matchesClient
        })
    }, [quotation, filters, selectedSellerId, selectedClientId])

    const filteredStatusCount = useMemo(() => {
        const count: Record<string, number> = {}

        quotation?.forEach((q) => {
            const matchesSeller = selectedSellerId
                ? q.seller_id === selectedSellerId
                : true
            const matchesClient = selectedClientId
                ? q.client_id === selectedClientId
                : true

            if (matchesSeller && matchesClient) {
                const label = convertStatus(q.status_name)
                count[label] = (count[label] || 0) + 1
            }
        })

        return count
    }, [quotation, selectedSellerId, selectedClientId])

    const handleCheckboxChange = (label: string, checked: boolean) => {
        setLoadingFilters(true)
        setTimeout(() => {
            const newFilters = new Set(filters)
            if (checked) {
                newFilters.add(label)
            } else {
                newFilters.delete(label)
            }
            setFilters(newFilters)
            setLoadingFilters(false)
        }, 400)
    }

    const sellerOptions = Array.from(
        new Map(
            (quotation || []).map((q) => [
                q.seller_id,
                { id: q.seller_id, name: q.seller_name },
            ])
        ).values()
    ).sort((a, b) => a.name.localeCompare(b.name))

    const clientOptions = Array.from(
        new Map(
            (quotation || []).map((q) => [
                q.client_id,
                { id: q.client_id, name: q.client_name },
            ])
        ).values()
    ).sort((a, b) => a.name.localeCompare(b.name))

    return (
        <div
            className="modalQuotationContainer"
            style={{
                display: 'flex',
                padding: '10px 30px 30px',
                flexDirection: 'column',
                minHeight: '25vh',
                justifyContent: 'space-between',
                margin: '0 auto',
            }}
        >
            {!loadingTable && (
                <div className="headerContainer">
                    <h2 className="titleQuotation">Cotações</h2>

                    <div className="divFiltersQuotation">
                        <div className="selectsSellerClient">
                            <div className="selectsContainer">
                                <div>
                                    <Select
                                        description="Cliente"
                                        value={selectedClientId}
                                        value_key="id"
                                        label_key="name"
                                        blank_label="Selecione o cliente"
                                        options={clientOptions}
                                        onChange={(e: any) => {
                                            setLoadingFilters(true)
                                            setSelectedClientId(e.target.value)
                                            setTimeout(() => {
                                                setLoadingFilters(false)
                                            }, 400)
                                        }}
                                    />
                                </div>
                                <div>
                                    <Select
                                        description="Vendedor"
                                        value={selectedSellerId}
                                        value_key="id"
                                        label_key="name"
                                        blank_label="Selecione o vendedor"
                                        options={sellerOptions}
                                        onChange={(e: any) => {
                                            setLoadingFilters(true)
                                            setSelectedSellerId(e.target.value)
                                            setTimeout(() => {
                                                setLoadingFilters(false)
                                            }, 400)
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="buttonClearFilters">
                                <ButtonV3
                                    title="Limpar Filtros"
                                    onClick={() => {
                                        setLoadingFilters(true)
                                        setSelectedSellerId('')
                                        setSelectedClientId('')
                                        setFilters(
                                            new Set(['VENCEDOR', 'PERDEDOR'])
                                        )
                                        setTimeout(() => {
                                            setLoadingFilters(false)
                                        }, 400)
                                    }}
                                    style={{ whiteSpace: 'nowrap' }}
                                />
                            </div>
                        </div>

                        <div className="divLabelsContent">
                            {Object.entries(filteredStatusCount).map(
                                ([label, count]) => (
                                    <StatusLabel
                                        key={label}
                                        label={label}
                                        count={count}
                                        isChecked={filters.has(label)}
                                        onChange={handleCheckboxChange}
                                    />
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="NewListCotacoes">
                {loadingTable ? (
                    <Loader type="inline" />
                ) : (
                    <>
                        {filteredQuotations && filteredQuotations.length > 0 ? (
                            <table>
                                {loadingFilters && <Loader />}
                                <thead>
                                    <tr>
                                        <th>Cotação</th>
                                        <th>Data</th>
                                        <th>Cliente</th>
                                        <th>Vendedor</th>
                                        <th>Marca</th>
                                        <th>Status</th>
                                        <th>Preço Digitado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredQuotations.map((item, index) => (
                                        <tr key={index}>
                                            <td
                                                data-label="Cotação"
                                                style={{ fontWeight: 500 }}
                                            >
                                                <span>#</span>
                                                {item.quotation_code}
                                            </td>
                                            <td data-label="Data">
                                                {item.date
                                                    ? new Date(
                                                          item.date
                                                      ).toLocaleDateString(
                                                          'pt-BR'
                                                      )
                                                    : ''}
                                            </td>
                                            <td data-label="Cliente">
                                                {item.client_name ?? ''}
                                            </td>
                                            <td data-label="Vendedor">
                                                {item.seller_name ?? ''}
                                            </td>
                                            <td data-label="Marca">
                                                {item.product_brand ?? ''}
                                            </td>
                                            <td data-label="Status">
                                                <p
                                                    style={{
                                                        color: statusColors[
                                                            convertStatus(
                                                                item.status_name
                                                            )
                                                        ],
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {convertStatus(
                                                        item.status_name
                                                    )}
                                                </p>
                                            </td>
                                            <td data-label="Preço Digitado">
                                                <LabelPrice
                                                    color={`${
                                                        item.status_name ===
                                                        'LOSER'
                                                            ? item.winner_price !=
                                                              0
                                                                ? item.color
                                                                : '#820bbd'
                                                            : 'DEFAULT'
                                                    }`}
                                                    price={item.quotation_price}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <FeedbackError message="Nenhuma cotação foi encontrada..." />
                        )}
                    </>
                )}
            </div>
            {!loadingTable && (
                <div
                    className="legendQuotation"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: 10,
                    }}
                >
                    <Legend />
                    <div>
                        <ButtonV3 filled title="Voltar" onClick={onClose} />
                    </div>
                </div>
            )}
        </div>
    )
}
